'use client';

import { Exhibition } from "@/app/lib/generated/graphql-types";
import {
    ExhibitionsOverviewHeaderFrame,
    ExhibitionsOverviewIntroFrame,
    ExhibitionsOverviewTileCardFrame,
    ExhibitionsOverviewTileImageFrame,
    ExhibitionsOverviewTilesFrame
} from "@/app/features/sites/exhibitions/ui/shared/ExhibitionsOverviewFrames";
import SlideshowIntroV2 from "@/app/__to_be_integrated/_____components/_NEW/intros/slideshow/SlideshowIntroV2";
import { notNull } from "@/app/lib/common/not-null";
import { Box, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import styles from "@/app/features/sites/guided-tours/ui/public/guided-tours-public.module.css";
import EditableTypographyV2 from "@/app/features/shared/editable-typography/EditableTypographyV2";
import EditableMarkdownV2 from "@/app/features/shared/editable-markdown/EditableMarkdownV2";
import ExhibitionOverviewItemEditorProvider, {
    useExhibitionOverviewItemEditor
} from "@/app/features/sites/exhibitions/contexts/ExhibitionOverviewItemEditorContext";
import Image from "next/image";
import { mediaRefAlt, mediaRefUrl } from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";
import clsx from "clsx";
import EditMediaButtonOverlay from "@/app/features/shared/EditMediaButtonOverlay";
import { PLACEHOLDER_IMAGE } from "@/app/types/common/constants";

interface ExhibitionsOverviewEditableProps {
    data: Exhibition[];
}

const EXCERPT_EDITOR_HEIGHT = { xs: 260, md: 250 };

export default function ExhibitionsOverviewEditable({ data }: ExhibitionsOverviewEditableProps) {
    const promoImages = data
        .filter((exhibition) => !!exhibition.promoImage)
        .map((exhibition) => exhibition.promoImage!)
        .filter(notNull);

    return (
        <>
            <ExhibitionsOverviewIntroFrame>
                <SlideshowIntroV2 title="Ausstellungen" images={promoImages} heightPreset="reduced" />
            </ExhibitionsOverviewIntroFrame>

            <ExhibitionsOverviewHeaderFrame>
                <Typography component="h2" variant="h1" className="text-wrap md:text-nowrap">
                    Unsere Dauerausstellungen
                </Typography>
            </ExhibitionsOverviewHeaderFrame>

            <ExhibitionsOverviewTilesFrame>
                {data.map((exhibition) => (
                    <Grid key={exhibition.documentId} size={1}>
                        <ExhibitionOverviewItemEditorProvider documentId={exhibition.documentId}>
                            <ExhibitionOverviewTileEditable />
                        </ExhibitionOverviewItemEditorProvider>
                    </Grid>
                ))}
            </ExhibitionsOverviewTilesFrame>
        </>
    )
}

function ExhibitionOverviewTileEditable() {
    const { editable, updateEditable, errorForField } = useExhibitionOverviewItemEditor();

    if (!editable) return null;

    const titleError = errorForField?.('title') || undefined;
    const excerptError = errorForField?.('excerpt') || undefined;
    const promoImage = editable.promoImage ?? null;
    const promoImageSrc = promoImage ? mediaRefUrl(promoImage) : PLACEHOLDER_IMAGE;
    const promoImageAlt = promoImage ? (mediaRefAlt(promoImage) ?? editable.title) : editable.title;

    return (
        <ExhibitionsOverviewTileCardFrame animated={false}>
            <Stack spacing={2} className="flex flex-auto">
                <ExhibitionsOverviewTileImageFrame>
                    <Image
                        src={promoImageSrc}
                        alt={promoImageAlt}
                        fill
                        loading="lazy"
                        quality={75}
                        sizes="(max-width: calc(var(--content-max-width) / 3)) 100vw, 33vw"
                        className={clsx('signature-border-radius', styles.tileImage)}
                    />
                    <EditMediaButtonOverlay
                        onImageChange={(promoImage) => updateEditable({ promoImage })}
                    />
                </ExhibitionsOverviewTileImageFrame>

                <Stack spacing={2} className="md:px-4 pb-2">
                    <EditableTypographyV2
                        label="Titel"
                        variant="h2"
                        color="primary"
                        className={styles.tileTitle}
                        value={editable.title}
                        onChange={(title) => updateEditable({ title })}
                        error={Boolean(titleError)}
                        helperText={titleError}
                    />

                    <Box
                        sx={{
                            display: "flex",
                            height: EXCERPT_EDITOR_HEIGHT,
                            minHeight: EXCERPT_EDITOR_HEIGHT,
                            maxHeight: EXCERPT_EDITOR_HEIGHT,
                            minWidth: 0,
                            overflow: "hidden",
                        }}
                    >
                        <EditableMarkdownV2
                            label="Kurzbeschreibung"
                            value={editable.excerpt}
                            onChange={(excerpt) => updateEditable({ excerpt })}
                            typographyOverrides={{ paragraph: 'body2' }}
                            error={Boolean(excerptError)}
                            helperText={excerptError}
                            slots={{
                                root: {
                                    sx: {
                                        display: "flex",
                                        height: "100%",
                                        maxHeight: "100%",
                                        width: "100%",
                                        minHeight: 0,
                                        minWidth: 0,
                                        "& > [role='button']": {
                                            boxSizing: "border-box",
                                            display: "flex",
                                            flexDirection: "column",
                                            height: "100%",
                                            maxHeight: "100%",
                                            width: "100%",
                                            overflowY: "auto",
                                            overflowX: "hidden",
                                        },
                                    },
                                },
                                markdown: {
                                    root: {
                                        sx: {
                                            height: "100%",
                                            maxHeight: "100%",
                                            minHeight: 0,
                                            overflow: "hidden",
                                        },
                                    },
                                },
                            }}
                        />
                    </Box>
                </Stack>
            </Stack>
        </ExhibitionsOverviewTileCardFrame>
    );
}
